import { Icon, Icons } from '@/components/icon';
import { useTranslation } from '@/hooks/use-translation';
import { cn } from '@/lib/utils';
import { useEffect, useRef, useState } from 'react';
import { Combobox, ComboboxGroup, ComboboxItem } from '../ui/combobox';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { P3 } from '../ui/text';

export type FilterCondition =
  | 'contains'
  | 'not_contains'
  | 'starts_with'
  | 'ends_with'
  | 'is'
  | 'is_not'
  | 'greater_than'
  | 'greater_than_or_equal'
  | 'less_than'
  | 'less_than_or_equal'
  | 'empty'
  | 'not_empty'
  | 'before'
  | 'after'
  | 'date_equals'
  | 'from'
  | 'to'
  | 'has';

export interface FilterValue {
  id: string;
  columnId: string;
  columnLabel: string;
  columnIcon?: keyof typeof Icons;
  condition: FilterCondition | null;
  value?: string;
  /** Override field sent to server (used for columns with custom conditions) */
  serverField?: string;
}

export interface ColumnFilterConfig {
  customConditions?: { value: string; labelKey: string; serverField: string }[];
  renderValueInput?: (props: {
    condition: string;
    value: string;
    onChange: (value: string) => void;
    autoOpen?: boolean;
  }) => React.ReactNode;
}

const conditionKeys = [
  { value: 'starts_with', key: 'startsWith' },
  { value: 'is', key: 'is' },
  { value: 'contains', key: 'contains' },
  { value: 'not_contains', key: 'notContains' },
  { value: 'ends_with', key: 'endsWith' },
  { value: 'is_not', key: 'isNot' },
  { value: 'greater_than', key: 'greaterThan' },
  { value: 'greater_than_or_equal', key: 'greaterThanOrEqual' },
  { value: 'less_than', key: 'lessThan' },
  { value: 'less_than_or_equal', key: 'lessThanOrEqual' },
  { value: 'empty', key: 'empty' },
  { value: 'not_empty', key: 'notEmpty' },
  { value: 'before', key: 'before' },
  { value: 'after', key: 'after' },
  { value: 'date_equals', key: 'dateEquals' },
  { value: 'has', key: 'has' },
] as const;

const conditionsWithoutValue: FilterCondition[] = ['empty', 'not_empty'];

interface FilterChipProps {
  filter: FilterValue;
  availableColumns?: ColumnOption[];
  onColumnChange?: (columnId: string) => void;
  onConditionChange?: (condition: FilterCondition, serverField?: string) => void;
  onValueChange?: (value: string) => void;
  /** Called on blur to sync input value to parent state without triggering server filter */
  onInputSync?: (value: string) => void;
  onRemove?: () => void;
  className?: string;
  /** Custom configuration for this column's filter behavior */
  columnConfig?: ColumnFilterConfig;
}

interface ColumnOption {
  value: string;
  label: string;
  icon?: keyof typeof Icons;
  conditions?: FilterCondition[];
  inputType?: string;
}

export function FilterChip({
  filter,
  availableColumns = [],
  onColumnChange,
  onConditionChange,
  onValueChange,
  onInputSync,
  onRemove,
  className,
  columnConfig,
}: FilterChipProps) {
  const { t } = useTranslation('desktop_ui');
  const [inputValue, setInputValue] = useState(filter.value ?? '');
  const [editingValue, setEditingValue] = useState('');
  const [valuePopoverOpen, setValuePopoverOpen] = useState(false);
  const [deleteMenuOpen, setDeleteMenuOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const selectTriggerRef = useRef<HTMLButtonElement>(null);
  const initialFilterIdRef = useRef(filter.id);
  const prevColumnIdRef = useRef(filter.columnId);
  const prevConditionRef = useRef(filter.condition);

  // Determine if we're using custom conditions
  const hasCustomConditions = !!columnConfig?.customConditions;
  const hasCustomValueInput = !!columnConfig?.renderValueInput;

  // Sync inputValue when filter.value changes externally (e.g., after filter deletion)
  useEffect(() => {
    setInputValue(filter.value ?? '');
  }, [filter.value]);

  // Pre-compute all translations OUTSIDE of portals to avoid React context conflicts
  // when Radix removes portal content while i18n context tries to update
  const searchAttributesText = t('components.contacts.filter.searchAttributes');
  const selectConditionText = t('components.contacts.filter.selectCondition');
  const enterTextPlaceholder = t('components.contacts.filter.enterText');
  const deleteFilterText = t('components.contacts.filter.deleteFilter');
  const defaultConditionLabels: Record<string, string> = {
    contains: t('components.contacts.filter.conditions.contains'),
    not_contains: t('components.contacts.filter.conditions.notContains'),
    starts_with: t('components.contacts.filter.conditions.startsWith'),
    ends_with: t('components.contacts.filter.conditions.endsWith'),
    is: t('components.contacts.filter.conditions.is'),
    is_not: t('components.contacts.filter.conditions.isNot'),
    greater_than: t('components.contacts.filter.conditions.greaterThan'),
    greater_than_or_equal: t('components.contacts.filter.conditions.greaterThanOrEqual'),
    less_than: t('components.contacts.filter.conditions.lessThan'),
    less_than_or_equal: t('components.contacts.filter.conditions.lessThanOrEqual'),
    empty: t('components.contacts.filter.conditions.empty'),
    not_empty: t('components.contacts.filter.conditions.notEmpty'),
    before: t('components.contacts.filter.conditions.before'),
    after: t('components.contacts.filter.conditions.after'),
    date_equals: t('components.contacts.filter.conditions.dateEquals'),
    has: t('components.contacts.filter.conditions.has'),
  };

  // Build custom condition labels if provided
  const customConditionLabels: Record<string, string> = {};
  if (columnConfig?.customConditions) {
    for (const cond of columnConfig.customConditions) {
      customConditionLabels[cond.value] = t(
        `components.companies.filter.activity.conditions.${cond.labelKey}` as never,
      );
    }
  }

  // Use custom condition labels if available, otherwise use default
  const conditionLabels = hasCustomConditions ? customConditionLabels : defaultConditionLabels;

  // Get available conditions and input type for the current column
  const currentColumn = availableColumns.find((col) => col.value === filter.columnId);
  const columnConditions = currentColumn?.conditions;
  const valueInputType = currentColumn?.inputType ?? 'text';
  const filteredConditionKeys = columnConditions
    ? conditionKeys.filter((ck) => columnConditions.includes(ck.value as FilterCondition))
    : conditionKeys;

  // Open condition select for new filters or when column changes
  // Using a ref + click instead of controlled open state to avoid conflicts with Radix
  // Initialize to true if filter already has a condition (not a new filter)
  const hasOpenedSelectRef = useRef(filter.condition !== null);

  useEffect(() => {
    // If filter ID changed, this component is being reused - reset and don't open
    if (initialFilterIdRef.current !== filter.id) {
      initialFilterIdRef.current = filter.id;
      prevColumnIdRef.current = filter.columnId;
      hasOpenedSelectRef.current = filter.condition !== null;
      return;
    }

    const columnChanged = prevColumnIdRef.current !== filter.columnId;
    const isNewFilter = filter.condition === null && !hasOpenedSelectRef.current;

    prevColumnIdRef.current = filter.columnId;

    if (columnChanged || isNewFilter) {
      hasOpenedSelectRef.current = true;
      // Small delay to let previous interactions settle
      setTimeout(() => {
        selectTriggerRef.current?.click();
      }, 50);
    }
  }, [filter.id, filter.columnId, filter.condition]);

  // For custom conditions, all conditions require a value input
  // For default conditions, exclude 'empty' and 'not_empty'
  const showValueInput =
    filter.condition && (hasCustomConditions || !conditionsWithoutValue.includes(filter.condition));

  // Track auto-open for custom value inputs
  const [customValueAutoOpen, setCustomValueAutoOpen] = useState(false);

  // Open value popover when condition changes to a value-requiring condition (only if no value exists)
  useEffect(() => {
    const conditionChanged = prevConditionRef.current !== filter.condition;
    prevConditionRef.current = filter.condition;

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (conditionChanged && showValueInput && !inputValue) {
      if (hasCustomValueInput) {
        // Reset first so the effect in the custom input can re-trigger
        setCustomValueAutoOpen(false);
        // Signal custom value input to auto-open
        timeoutId = setTimeout(() => {
          setCustomValueAutoOpen(true);
        }, 50);
      } else {
        // Small delay to ensure popover is ready
        timeoutId = setTimeout(() => {
          setValuePopoverOpen(true);
        }, 50);
      }
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [filter.condition, showValueInput, inputValue, hasCustomValueInput]);

  // Initialize editing value and auto-focus input when value popover opens
  const prevValuePopoverOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = valuePopoverOpen && !prevValuePopoverOpenRef.current;
    prevValuePopoverOpenRef.current = valuePopoverOpen;

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (justOpened) {
      setEditingValue(inputValue);
      timeoutId = setTimeout(() => inputRef.current?.focus(), 0);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [valuePopoverOpen, inputValue]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingValue(e.target.value);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!editingValue.trim()) {
        return;
      }
      setInputValue(editingValue);
      onValueChange?.(editingValue);
      setValuePopoverOpen(false);
    }
  };

  const handleValuePopoverOpenChange = (open: boolean) => {
    setValuePopoverOpen(open);
    // Save editing value when popover closes (without triggering server filter)
    if (!open && editingValue !== inputValue) {
      setInputValue(editingValue);
      onInputSync?.(editingValue);
    }
  };

  const handleDelete = () => {
    setDeleteMenuOpen(false);
    onRemove?.();
  };

  return (
    <div
      className={cn(
        'flex h-7 shrink-0 items-center rounded-full border ml-0.5 border-primary-10 bg-primary-3 overflow-hidden',
        className,
      )}
    >
      <Combobox
        variant="filter"
        searchPlaceholder={searchAttributesText}
        options={[
          { value: filter.columnId, label: filter.columnLabel, icon: filter.columnIcon },
          ...availableColumns.filter((col) => col.value !== filter.columnId),
        ]}
        value={filter.columnId}
        onValueChange={(value) => onColumnChange?.(value)}
        trigger={
          <button
            type="button"
            className="h-full flex cursor-pointer items-center gap-1 border-r border-primary-10 pl-3 pr-2 outline-none hover:bg-primary-5 data-[state=open]:bg-primary-5"
          >
            <Icon
              name={filter.columnIcon ? Icons[filter.columnIcon] : Icons.Hash}
              className="text-s text-primary-80"
            />
            <P3 className="whitespace-nowrap text-primary-100">{filter.columnLabel}</P3>
          </button>
        }
      >
        {({ filteredOptions }) => (
          <ComboboxGroup variant="filter">
            {filteredOptions.map((option) => {
              const colOption = option as ColumnOption;
              return (
                <ComboboxItem key={option.value} option={option} variant="filter">
                  {(opt, _isSelected) => (
                    <div className="flex items-center gap-1">
                      {colOption.icon && (
                        <Icon name={Icons[colOption.icon]} className="text-s text-primary-80" />
                      )}
                      <P3 className="text-primary-100">{opt.label}</P3>
                    </div>
                  )}
                </ComboboxItem>
              );
            })}
          </ComboboxGroup>
        )}
      </Combobox>

      <Select
        value={filter.condition ?? ''}
        onValueChange={(value) => {
          // Find the serverField for custom conditions
          const customCondition = columnConfig?.customConditions?.find((c) => c.value === value);
          const serverField = customCondition?.serverField;
          onConditionChange?.(value as FilterCondition, serverField);
        }}
      >
        <SelectTrigger
          ref={selectTriggerRef}
          showChevron={false}
          className="h-full text-s min-h-0 border-0 border-r border-primary-10 rounded-none shadow-none px-2 outline-none cursor-pointer hover:bg-primary-5 data-[state=open]:bg-primary-5 focus:ring-0 [&_[data-slot=select-value]]:gap-0 [&_[data-slot=select-value]]:text-s [&_[data-slot=select-value]]:text-primary-100 [&_[data-slot=select-value]]:font-medium"
        >
          <SelectValue placeholder={selectConditionText}>
            <P3 className={filter.condition ? 'text-primary-100' : 'text-primary-60'}>
              {filter.condition ? conditionLabels[filter.condition] : selectConditionText}
            </P3>
          </SelectValue>
        </SelectTrigger>
        <SelectContent
          showChevron={false}
          className="w-[179px] min-w-0 rounded-2xl border border-primary-5 bg-white p-2 shadow-md [&_[data-slot=select-viewport]]:space-y-1"
        >
          {hasCustomConditions && columnConfig?.customConditions
            ? [...columnConfig.customConditions]
                .sort((a, b) => {
                  if (a.value === filter.condition) {
                    return -1;
                  }
                  if (b.value === filter.condition) {
                    return 1;
                  }
                  return 0;
                })
                .map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    className="min-h-6 cursor-pointer rounded-md px-1 py-0 pr-8 text-s font-medium text-primary-100 outline-none data-[highlighted]:bg-primary-3"
                  >
                    {conditionLabels[option.value]}
                  </SelectItem>
                ))
            : [...filteredConditionKeys]
                .sort((a, b) => {
                  if (a.value === filter.condition) {
                    return -1;
                  }
                  if (b.value === filter.condition) {
                    return 1;
                  }
                  return 0;
                })
                .map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    className="min-h-6 cursor-pointer rounded-md px-1 py-0 pr-8 text-s font-medium text-primary-100 outline-none data-[highlighted]:bg-primary-3"
                  >
                    {conditionLabels[option.value]}
                  </SelectItem>
                ))}
        </SelectContent>
      </Select>

      {showValueInput && hasCustomValueInput && filter.condition && columnConfig?.renderValueInput
        ? columnConfig.renderValueInput({
            condition: filter.condition,
            value: inputValue,
            onChange: (value) => {
              setInputValue(value);
              onValueChange?.(value);
            },
            autoOpen: customValueAutoOpen,
          })
        : showValueInput && (
            <Popover open={valuePopoverOpen} onOpenChange={handleValuePopoverOpenChange}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="h-full min-w-16 max-w-32 border-r border-primary-10 bg-transparent px-2 outline-none cursor-pointer hover:bg-primary-5 data-[state=open]:bg-primary-5 text-left truncate"
                >
                  <P3
                    className={cn('truncate', inputValue ? 'text-primary-100' : 'text-primary-60')}
                  >
                    {inputValue || enterTextPlaceholder}
                  </P3>
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-auto rounded-2xl border border-primary-5 bg-white p-2 shadow-md"
              >
                <input
                  ref={inputRef}
                  type={valueInputType}
                  value={editingValue}
                  onChange={handleInputChange}
                  onKeyDown={handleInputKeyDown}
                  placeholder={enterTextPlaceholder}
                  className="w-48 rounded-md border border-primary-10 bg-transparent px-2 py-1 font-inter text-s font-medium text-primary-100 placeholder:text-primary-60 outline-none focus:border-accent [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
                />
              </PopoverContent>
            </Popover>
          )}

      <Popover open={deleteMenuOpen} onOpenChange={setDeleteMenuOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="h-full flex shrink-0 cursor-pointer items-center px-2 justify-center text-primary-60 outline-none hover:bg-primary-5 data-[state=open]:bg-primary-5 hover:text-primary-50"
          >
            <Icon name={Icons.DotsThree} className="text-s rotate-90" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-auto rounded-2xl border border-primary-5 bg-white p-2 shadow-md"
        >
          <button
            type="button"
            onClick={handleDelete}
            className="flex h-6 cursor-pointer items-center gap-1 rounded-md px-1 text-primary-100 outline-none hover:bg-primary-5"
          >
            <Icon name={Icons.Bin} className="text-s text-primary-80" />
            <P3 className="text-primary-100">{deleteFilterText}</P3>
          </button>
        </PopoverContent>
      </Popover>
    </div>
  );
}
